import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { LogOut, Trash2, ImagePlus, Check, GripVertical } from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import { api, imgUrl, formatApiError } from "@/api";
import ClientGalleriesManager from "@/pages/admin/ClientGalleries";
import { FadeUp } from "@/components/Reveal";

const STATUS_STYLES = {
  new: "border-flame/60 text-flame",
  confirmed: "border-emerald-500/60 text-emerald-400",
  archived: "border-white/20 text-faint",
};

const inputCls =
  "w-full bg-transparent border-b border-white/15 focus:border-flame outline-none py-2.5 text-sm text-bone placeholder:text-faint transition-colors duration-300";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-faint mb-1";

function AdminPhotoCard({ p, val, setField, save, removePhoto, edits }) {
  const controls = useDragControls();
  return (
    <Reorder.Item
      as="div"
      value={p}
      dragListener={false}
      dragControls={controls}
      className="border border-white/10 bg-coal"
      data-testid={`admin-photo-${p.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={imgUrl(p.thumb)} alt={p.title} loading="lazy" draggable={false} className="h-full w-full object-cover pointer-events-none" />
        <button
          data-testid={`photo-drag-${p.id}`}
          onPointerDown={(e) => controls.start(e)}
          style={{ touchAction: "none" }}
          aria-label="Drag to reorder"
          className="absolute top-2 right-2 p-2 bg-ink/70 border border-white/15 text-ash hover:text-flame hover:border-flame transition-colors cursor-grab active:cursor-grabbing"
        >
          <GripVertical size={14} />
        </button>
      </div>
      <div className="p-4 space-y-3">
        <div>
          <label className={labelCls}>Title</label>
          <input
            data-testid={`photo-title-${p.id}`}
            value={val(p, "title")}
            onChange={(e) => setField(p.id, "title", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Label</label>
          <input
            data-testid={`photo-subcategory-${p.id}`}
            value={val(p, "subcategory")}
            onChange={(e) => setField(p.id, "subcategory", e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className={labelCls}>Category</label>
          <select
            data-testid={`photo-category-${p.id}`}
            value={val(p, "category")}
            onChange={(e) => setField(p.id, "category", e.target.value)}
            className={`${inputCls} bg-ink`}
          >
            <option value="automotive">Automotive</option>
            <option value="personal">Personal / Portrait</option>
          </select>
        </div>
        <div className="flex items-center justify-between pt-1">
          {edits[p.id] ? (
            <button
              data-testid={`photo-save-${p.id}`}
              onClick={() => save(p.id)}
              className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-flame hover:text-flamebright transition-colors"
            >
              <Check size={12} /> Save changes
            </button>
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-faint">{p.specs}</span>
          )}
          <button
            data-testid={`photo-delete-${p.id}`}
            onClick={() => removePhoto(p.id)}
            className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-faint hover:text-flame transition-colors"
          >
            <Trash2 size={12} /> Remove
          </button>
        </div>
      </div>
    </Reorder.Item>
  );
}

function PhotosManager({ headers }) {
  const [photos, setPhotos] = useState([]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("automotive");
  const [subcategory, setSubcategory] = useState("");
  const [uploading, setUploading] = useState(false);
  const [edits, setEdits] = useState({});
  const [savedOrder, setSavedOrder] = useState([]);
  const fileRef = useRef(null);

  const loadPhotos = useCallback(async () => {
    try {
      const { data } = await api.get("/photos");
      setPhotos(data);
      setSavedOrder(data.map((p) => p.id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview((old) => {
      if (old) URL.revokeObjectURL(old);
      return f ? URL.createObjectURL(f) : null;
    });
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, "").replace(/[-_]/g, " "));
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose a photo first");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("category", category);
      fd.append("subcategory", subcategory);
      const { data } = await api.post("/photos/upload", fd, { headers });
      setPhotos((ps) => [...ps, data]);
      setSavedOrder((o) => [...o, data.id]);
      setFile(null);
      setPreview(null);
      setTitle("");
      setSubcategory("");
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Photo added to the gallery");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const val = (p, k) => edits[p.id]?.[k] ?? p[k];
  const setField = (id, k, v) => setEdits((e) => ({ ...e, [id]: { ...(e[id] || {}), [k]: v } }));

  const dirty = photos.map((p) => p.id).join() !== savedOrder.join();

  const saveOrder = async () => {
    try {
      await api.put("/photos/order", { order: photos.map((p) => p.id) }, { headers });
      setSavedOrder(photos.map((p) => p.id));
      toast.success("Gallery order saved");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async (id) => {
    try {
      const { data } = await api.patch(`/photos/${id}`, edits[id], { headers });
      setPhotos((ps) => ps.map((p) => (p.id === id ? data : p)));
      setEdits((e) => {
        const n = { ...e };
        delete n[id];
        return n;
      });
      toast.success("Photo updated");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const removePhoto = async (id) => {
    try {
      await api.delete(`/photos/${id}`, { headers });
      setPhotos((ps) => ps.filter((p) => p.id !== id));
      setSavedOrder((o) => o.filter((x) => x !== id));
      toast.success("Photo removed from gallery");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <form onSubmit={upload} className="border border-white/10 bg-coal p-6 sm:p-8 mb-12" data-testid="photo-upload-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-6">Add a new frame</p>
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <label
              htmlFor="up-file"
              data-testid="photo-upload-dropzone"
              className="block border border-dashed border-white/20 hover:border-flame/60 transition-colors cursor-pointer aspect-[4/3] flex items-center justify-center overflow-hidden bg-ink/40"
            >
              {preview ? (
                <img src={preview} alt="Upload preview" className="h-full w-full object-cover" data-testid="photo-upload-preview" />
              ) : (
                <span className="flex flex-col items-center gap-3 text-faint">
                  <ImagePlus size={26} />
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em]">Choose photo</span>
                </span>
              )}
            </label>
            <input
              id="up-file"
              ref={fileRef}
              type="file"
              accept="image/*"
              data-testid="photo-upload-file-input"
              onChange={pickFile}
              className="hidden"
            />
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-x-8 gap-y-6 content-start">
            <div>
              <label className={labelCls} htmlFor="up-title">Title</label>
              <input id="up-title" data-testid="photo-upload-title-input" required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Midnight Vantage" className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="up-category">Category</label>
              <select id="up-category" data-testid="photo-upload-category-select" value={category} onChange={(e) => setCategory(e.target.value)} className={`${inputCls} bg-ink`}>
                <option value="automotive">Automotive</option>
                <option value="personal">Personal / Portrait</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} htmlFor="up-subcategory">Label / subcategory</label>
              <input id="up-subcategory" data-testid="photo-upload-subcategory-input" value={subcategory} onChange={(e) => setSubcategory(e.target.value)} placeholder="e.g. Car Meet / Night" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                disabled={uploading}
                data-testid="photo-upload-submit-button"
                className="bg-flame text-ink px-8 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-flamebright transition-colors disabled:opacity-50"
              >
                {uploading ? "Uploading…" : "Upload to gallery"}
              </button>
            </div>
          </div>
        </div>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-faint">
          {photos.length} frames live in the gallery — drag the handle to reorder
        </p>
        {dirty && (
          <button
            data-testid="photos-save-order-button"
            onClick={saveOrder}
            className="bg-flame text-ink px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-flamebright transition-colors"
          >
            Save order
          </button>
        )}
      </div>
      <Reorder.Group
        as="div"
        axis="y"
        values={photos}
        onReorder={setPhotos}
        className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5"
        data-testid="admin-photos-grid"
      >
        {photos.map((p) => (
          <AdminPhotoCard key={p.id} p={p} val={val} setField={setField} save={save} removePhoto={removePhoto} edits={edits} />
        ))}
      </Reorder.Group>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("mr_token"));
  const [email, setEmail] = useState("Maddietweetrbg@gmail.com");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("bookings");

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [b, s] = await Promise.all([
        api.get("/bookings", { headers }),
        api.get("/stats", { headers }),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("mr_token");
        setToken(null);
      }
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("mr_token", data.token);
      setToken(data.token);
    } catch (error) {
      setErr(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status }, { headers });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/bookings/${id}`, { headers });
      setBookings((bs) => bs.filter((b) => b.id !== id));
      load();
      toast.success("Booking deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const logout = () => {
    localStorage.removeItem("mr_token");
    setToken(null);
  };

  if (!token) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" data-testid="admin-login-page">
        <FadeUp className="w-full max-w-md">
          <form onSubmit={login} className="border border-white/10 bg-coal p-8 sm:p-10" data-testid="admin-login-form">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">Studio access</p>
            <h1 className="font-serif text-3xl text-bone mb-8">Maddie&apos;s dashboard</h1>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-faint mb-1" htmlFor="ad-email">Email</label>
            <input
              id="ad-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-flame outline-none py-3 text-sm text-bone mb-6 transition-colors"
            />
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-faint mb-1" htmlFor="ad-pass">Password</label>
            <input
              id="ad-pass"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/15 focus:border-flame outline-none py-3 text-sm text-bone mb-6 transition-colors"
            />
            {err && <p className="text-sm text-flame mb-4" data-testid="admin-login-error">{err}</p>}
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-login-button"
              className="w-full bg-flame text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-flamebright transition-colors disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </FadeUp>
      </main>
    );
  }

  return (
    <main className="min-h-screen" data-testid="admin-dashboard">
      <section className="px-4 sm:px-8 lg:px-16 py-12">
        <div className="flex items-end justify-between border-b border-white/10 pb-6 mb-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-flame mb-3">Studio</p>
            <h1 className="font-serif text-4xl text-bone">
              {{ bookings: "Booking requests", photos: "Gallery photos", clients: "Client galleries" }[tab]}
            </h1>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-ash hover:text-flame transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-10">
          {[["bookings", "Bookings"], ["photos", "Photos"], ["clients", "Client Galleries"]].map(([id, label]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-5 py-2.5 font-mono text-xs uppercase tracking-[0.2em] border transition-colors duration-300 ${
                tab === id ? "border-flame bg-flame text-ink" : "border-white/15 text-ash hover:border-flame/60 hover:text-bone"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "photos" && <PhotosManager headers={headers} />}
        {tab === "clients" && <ClientGalleriesManager headers={headers} />}

        {tab === "bookings" && (
          <>
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12" data-testid="admin-stats">
            {[
              ["Total", stats.total],
              ["New", stats.new],
              ["Confirmed", stats.confirmed],
              ["Archived", stats.archived],
            ].map(([label, val]) => (
              <div key={label} className="border border-white/10 bg-coal p-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-faint">{label}</p>
                <p className="font-serif text-4xl text-bone mt-2" data-testid={`stat-${label.toLowerCase()}`}>{val}</p>
              </div>
            ))}
          </div>
        )}

        {bookings.length === 0 ? (
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-faint" data-testid="admin-empty">No booking requests yet</p>
        ) : (
          <div className="space-y-4">
            {bookings.map((b) => (
              <div key={b.id} className="border border-white/10 bg-coal p-6 grid lg:grid-cols-12 gap-6" data-testid={`booking-row-${b.id.slice(0, 8)}`}>
                <div className="lg:col-span-4">
                  <p className="font-serif text-xl text-bone">{b.name}</p>
                  <p className="font-mono text-xs text-ash mt-1">{b.email}</p>
                  <p className="font-mono text-xs text-ash">{b.phone}</p>
                </div>
                <div className="lg:col-span-4 font-mono text-xs text-ash space-y-1.5">
                  <p><span className="text-faint uppercase tracking-widest">Date</span> — {b.date}</p>
                  <p><span className="text-faint uppercase tracking-widest">Session</span> — {b.session_type}</p>
                  <p><span className="text-faint uppercase tracking-widest">Package</span> — {b.package}</p>
                  {b.photo_title && <p><span className="text-faint uppercase tracking-widest">Frame</span> — {b.photo_title}</p>}
                  {b.message && <p className="text-ash/80 normal-case font-sans text-sm pt-1">{b.message}</p>}
                </div>
                <div className="lg:col-span-4 flex lg:flex-col items-start lg:items-end gap-2">
                  <div className="flex flex-wrap gap-2">
                    {["new", "confirmed", "archived"].map((s) => (
                      <button
                        key={s}
                        data-testid={`status-${s}-${b.id.slice(0, 8)}`}
                        onClick={() => setStatus(b.id, s)}
                        className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] border transition-colors ${
                          b.status === s ? STATUS_STYLES[s] : "border-white/10 text-faint hover:text-ash"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                  <button
                    data-testid={`delete-${b.id.slice(0, 8)}`}
                    onClick={() => remove(b.id)}
                    className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-faint hover:text-flame transition-colors"
                  >
                    <Trash2 size={12} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
          </>
        )}
      </section>
    </main>
  );
}
